/**
 * Carte véhicule touchée (marketplace, landings, accueil, véhicules similaires) : légèrement assombrie
 * tout de suite et jusqu'à l'ouverture de la fiche, qui peut prendre 1 à 2 s sur réseau mobile. Même
 * rendu que la photo de la fiche (`is-pending-tap`, filtre luminosité 0,92).
 *
 * - Le doigt se pose : la carte s'assombrit après 80 ms (un défilement ou un glissé du carrousel
 *   photo, qui annulent l'appui, ne la font pas clignoter).
 * - Le doigt bouge de plus de 10 px, ou l'appui est annulé : la carte redevient claire.
 * - Le clic part vers la fiche : la carte reste assombrie jusqu'au départ de la page ; si rien ne
 *   se passe (lien ouvert ailleurs, navigation annulée), elle s'éclaircit au bout de 8 s, ou dès
 *   le retour sur la page (cache arrière du navigateur).
 * Boutons de la carte (flèches du carrousel, comparateur) : pas d'assombrissement de la carte.
 * Toucher seulement : à la souris, le survol fait déjà ce travail.
 */

export const CARD_PRESS_CLASS = "is-pending-tap";
/** Attente avant d'assombrir : un défilement se déclare avant (pointercancel). */
export const CARD_PRESS_DELAY_MS = 80;
/** Déplacement du doigt (px) au-delà duquel ce n'est plus un toucher. */
export const CARD_PRESS_MOVE_TOLERANCE = 10;
/** Durée maximale de l'état « ouverture en cours » si la page ne part pas. */
export const CARD_PRESS_MAX_MS = 8000;

const CARD = ".vehicle-card";
const IGNORED = "button, .nav-button, .vehicle-card__compare-favorite, [data-action]";

/** Carte concernée par un appui sur `target`, ou null (bouton de la carte, hors carte). */
export function pressedCard(target: EventTarget | null): HTMLElement | null {
    const el = target as Element | null;
    if (!el?.closest) return null;
    if (el.closest(IGNORED)) return null;
    return el.closest<HTMLElement>(CARD);
}

/** Le doigt a-t-il assez bougé pour que ce ne soit plus un toucher ? */
export function movedTooFar(
    start: { x: number; y: number },
    x: number,
    y: number,
    tolerance = CARD_PRESS_MOVE_TOLERANCE,
): boolean {
    return Math.hypot(x - start.x, y - start.y) > tolerance;
}

export function initCardPress(doc: Document = document): void {
    let card: HTMLElement | null = null;
    let start: { x: number; y: number } | null = null;
    let delay: number | undefined;
    let expiry: number | undefined;

    const release = () => {
        window.clearTimeout(delay);
        window.clearTimeout(expiry);
        expiry = undefined;
        card?.classList.remove(CARD_PRESS_CLASS);
        card = null;
        start = null;
    };

    doc.addEventListener(
        "pointerdown",
        (event) => {
            release();
            if (event.pointerType === "mouse") return;
            const target = pressedCard(event.target);
            if (!target) return;
            card = target;
            start = { x: event.clientX, y: event.clientY };
            delay = window.setTimeout(() => target.classList.add(CARD_PRESS_CLASS), CARD_PRESS_DELAY_MS);
        },
        { capture: true, passive: true },
    );
    doc.addEventListener(
        "pointermove",
        (event) => {
            if (card && start && movedTooFar(start, event.clientX, event.clientY)) release();
        },
        { capture: true, passive: true },
    );
    doc.addEventListener("pointercancel", release, true);
    // Doigt levé sans clic (appui long, geste annulé) : la carte s'éclaircit
    doc.addEventListener(
        "pointerup",
        () => {
            const pressed = card;
            window.setTimeout(() => {
                if (pressed && card === pressed && !expiry) release();
            }, 400);
        },
        true,
    );
    doc.addEventListener(
        "click",
        (event) => {
            const target = pressedCard(event.target);
            if (!target || target !== card) return;
            // La fiche s'ouvre : on garde l'état jusqu'au départ de la page
            window.clearTimeout(delay);
            target.classList.add(CARD_PRESS_CLASS);
            window.clearTimeout(expiry);
            expiry = window.setTimeout(release, CARD_PRESS_MAX_MS);
        },
        true,
    );
    window.addEventListener("pageshow", release);
    doc.addEventListener("visibilitychange", () => {
        if (doc.visibilityState === "visible") release();
    });
}

if (typeof document !== "undefined") initCardPress();
